/**
 * A tag entry can be either a plain string (legacy format from old TagTransformer)
 * or an object with name field (new format: { id, name, color }). id and color
 * are optional on the type so consumers that only need the display name do not
 * need to read them, but they may be present at runtime depending on which
 * endpoint produced the payload.
 */
export type tTagEntry =
  | string
  | { name: string; id?: number | string; color?: string };

/**
 * Extract the tag name string from a tag that may be either a plain string
 * or an object { name }. Returns empty string if name is missing.
 */
export function getTagName(tag: tTagEntry): string {
  if (typeof tag === 'string') return tag;
  return tag.name ?? '';
}

/**
 * Normalized form used to compare two tags: the panel treats ' VIP ' and 'vip'
 * as the same tag, and the server echoes back whichever casing it stored.
 */
export function normalizeTagName(tag: tTagEntry): string {
  return getTagName(tag).trim().toLowerCase();
}

/**
 * Remote tags plus any local tag the remote payload does not carry yet.
 * A lead payload can be older than a tag attached or removed since it was
 * emitted, and dropping the optimistic chip makes the tag look lost.
 */
export function mergeLocalTags<T extends tTagEntry>(
  remoteTags: T[],
  localTags: T[],
): T[] {
  const remoteNames = new Set(remoteTags.map(normalizeTagName));
  return [
    ...remoteTags,
    ...localTags.filter((tag) => !remoteNames.has(normalizeTagName(tag))),
  ];
}
